<template>
    <div class="position-relative">
        <div class="d-flex align-center justify-space-between gap-3">
            <div class="d-flex align-center">
                <v-icon
                    color="primary"
                    class="mb-1"
                    aria-hidden="true"
                >
                    mdi-account-group
                </v-icon>
                <h2 class="ml-2">Recipients</h2>
            </div>

            <div class="d-flex flex-wrap align-center gap-2">
                <selected-filters-summary
                    v-if="has_sent"
                    :bulk_message="bulk_message"
                />

                <v-btn
                    v-if="message_state === MESSAGE_STATES.REVIEW && !bulk_message.scheduled_send_time"
                    color="primary"
                    elevation="0"
                    small
                    outlined
                    @click="$emit('split-message')"
                >
                    <v-icon
                        aria-hidden="true"
                        small
                        left
                    >
                        mdi-call-split
                    </v-icon>
                    Split Message
                </v-btn>
            </div>
        </div>

        <v-card
            class="shaded-light mt-2 pb-0"
            flat
        >
            <v-card-text class="px-md-6">
                <!-- Loading State -->
                <template v-if="!progress_detail">
                    <div class="header-data">
                        <div
                            v-for="i in 4"
                            :key="i"
                            class="header-data-item header-data-item--loading"
                        >
                            <div class="d-flex flex-column align-center justify-center gap-1 full-width">
                                <v-skeleton-loader
                                    type="text"
                                    width="48"
                                    class="mb-1"
                                />
                                <v-skeleton-loader
                                    type="text"
                                    width="80"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="d-flex flex-column align-center mt-8 mb-4">
                        <v-skeleton-loader
                            type="avatar"
                            class="mb-2"
                        />
                        <v-skeleton-loader
                            type="text"
                            width="200"
                        />
                        <v-skeleton-loader
                            type="text"
                            width="200"
                            class="mt-2"
                        />
                    </div>
                </template>

                <template v-else>
                    <div class="header-data">
                        <v-tooltip
                            v-for="card in header_data_cards"
                            :key="card.key"
                            :disabled="!card.inactive && !card.admin_only"
                            max-width="250"
                            bottom
                        >
                            <template #activator="{ on, attrs }">
                                <article
                                    class="header-data-item"
                                    :class="{
                                        'header-data-item--inactive': card.inactive,
                                        'header-data-item--admin-only': card.admin_only
                                    }"
                                    :style="{
                                        background: card.background,
                                        border: `2px ${card.border_style || 'solid'} ${card.border}`
                                    }"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <div class="d-flex flex-column align-center justify-center gap-1 full-width">
                                        <transition-group
                                            name="number-change"
                                            tag="div"
                                            class="text-h4 font-weight-bold number-container"
                                        >
                                            <span :key="card.count">{{
                                                card.count.toLocaleString() }}</span>
                                        </transition-group>

                                        <div class="text-caption">
                                            {{ card.label }}
                                            <v-tooltip
                                                v-if="!card.inactive"
                                                max-width="300"
                                                bottom
                                            >
                                                <template #activator="{ on, attrs }">
                                                    <v-icon
                                                        color="grey darken-2"
                                                        class="ml-1"
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        x-small
                                                    >
                                                        mdi-information-outline
                                                    </v-icon>
                                                </template>
                                                <span>{{ card.tooltip }}</span>
                                            </v-tooltip>
                                        </div>
                                    </div>
                                </article>
                            </template>
                            <!-- TODO: temp -->
                            <span>{{ card.admin_only ? 'Staff Only' : card.inactive_reason }}</span>
                        </v-tooltip>
                    </div>

                    <!-- Prepare State -->
                    <transition name="fade">
                        <div
                            v-if="message_state === MESSAGE_STATES.PREPARE"
                            class="text-center mt-8 mb-2"
                        >
                            <v-progress-circular
                                size="32"
                                width="3"
                                class="mb-2"
                                indeterminate
                            />
                            <div class="text-h6 font-weight-medium">
                                Preparing Your Recipients
                            </div>
                            
                            <p class="text-body-2 mt-1 mb-0">
                                We're applying your filters to each recipient so you can review the results
                                <strong>before</strong> anything is sent.
                            </p>

                            <div class="d-flex flex-column flex-sm-row align-center justify-center gap-3 mt-4 mb-3">
                                <v-btn
                                    color="primary"
                                    outlined
                                    small
                                    @click="$emit('cancel-review')"
                                >
                                    <v-icon size="16" aria-hidden="true" left>mdi-cancel</v-icon>
                                    Cancel Preparation
                                </v-btn>

                                <span
                                    v-if="has_opted_into_ready_for_review_notification"
                                    class="d-inline-flex align-center success--text text-body-2"
                                >
                                    <v-icon size="18" color="success" class="ml-1 mr-2" aria-hidden="true">mdi-check-circle</v-icon>
                                    <strong>We'll notify you when ready</strong>
                                </span>

                                <v-btn
                                    v-else
                                    color="primary"
                                    elevation="0"
                                    :loading="submitting_notification_request"
                                    small
                                    @click="onRequestReadyForReviewNotification"
                                >
                                    <v-icon size="16" aria-hidden="true" left>mdi-bell-outline</v-icon>
                                    Notify Me When Ready
                                </v-btn>
                            </div>

                            <p class="text-caption mb-0">
                                <em>This may take a few minutes for larger lists. You don't have to stay on this page while waiting.</em>
                            </p>
                        </div>
                    </transition>

                    <!-- Review State -->
                    <transition name="slide-fade-up">
                        <div
                            v-if="message_state === MESSAGE_STATES.REVIEW"
                            class="text-center mt-8 mb-4"
                        >
                            <v-icon
                                color="success"
                                size="32"
                                class="mb-1"
                                aria-hidden="true"
                            >
                                mdi-check-circle
                            </v-icon>
                            <div class="text-h6 success--text font-weight-bold">
                                Recipients Ready for Review
                            </div>

                            <div class="my-4">
                                <v-simple-table
                                    class="filter-breakdown-table mx-auto"
                                    dense
                                >
                                    <thead>
                                        <tr>
                                            <th class="text-left">Reason Recipient Was Filtered</th>
                                            <th class="text-right">Count</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr v-if="count_in_state_detail_with_values.length === 0">
                                            <td
                                                colspan="2"
                                                class="text-center grey--text text--darken-1"
                                            >
                                                No recipients were filtered
                                            </td>
                                        </tr>
                                        <tr
                                            v-else
                                            v-for="row in filter_table_rows"
                                            :key="row.label"
                                        >
                                            <td class="text-left">{{ row.label }}</td>
                                            <td class="text-right">{{ row.value.toLocaleString() }}</td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                            </div>

                            <p class="text-body-2 mb-0">For complete details, <rc-link
                                    inline
                                    :open_in_new_tab="false"
                                    text_transform="none"
                                    class="text-body-2"
                                    @click="$emit('export-recipients-to-excel')"
                                >
                                    download the report
                                </rc-link>.
                            </p>
                        </div>
                    </transition>

                    <!-- Send State -->
                    <transition name="fade">
                        <div
                            v-if="message_state === MESSAGE_STATES.SEND"
                            class="text-center mt-8 mb-4"
                        >
                            <v-progress-circular
                                size="32"
                                width="3"
                                indeterminate
                                class="mb-3"
                            />
                            <div class="text-body-1 font-weight-medium">
                                Sending in Progress
                            </div>

                            <div class="mt-4">
                                <v-btn
                                    color="primary"
                                    class="mr-3"
                                    outlined
                                    @click="onClickCancel"
                                >
                                    <v-icon left>mdi-cancel</v-icon>
                                    Cancel Sending
                                </v-btn>
                                <v-btn
                                    color="primary"
                                    elevation="0"
                                    @click="onPause"
                                >
                                    <v-icon
                                        aria-hidden="true"
                                        left
                                    >
                                        mdi-pause
                                    </v-icon>
                                    Pause Sending
                                </v-btn>
                            </div>
                        </div>
                    </transition>

                    <!-- Paused State -->
                    <transition name="fade">
                        <div
                            v-if="message_state === MESSAGE_STATES.PAUSED"
                            class="text-center mt-8 mb-4"
                        >
                            <v-icon
                                size="32"
                                class="mb-0"
                                aria-hidden="true"
                            >
                                mdi-pause-circle
                            </v-icon>
                            <div class="text-body-1 font-weight-medium mt-1">
                                Sending Paused
                            </div>

                            <div class="my-4">
                                <v-btn
                                    color="primary"
                                    class="mr-3"
                                    outlined
                                    @click="onClickCancel"
                                >
                                    <v-icon left>mdi-cancel</v-icon>
                                    Cancel Sending
                                </v-btn>
                                <v-btn
                                    color="primary"
                                    elevation="0"
                                    @click="onResume"
                                >
                                    <v-icon left>mdi-play</v-icon>
                                    Resume Sending
                                </v-btn>
                            </div>

                            <p class="text-body-2 mt-1 mb-0">To view all recipients and their current status, <rc-link
                                    inline
                                    :open_in_new_tab="false"
                                    text_transform="none"
                                    class="text-body-2"
                                    @click="$emit('export-recipients-to-excel')"
                                >
                                    download the report
                                </rc-link>.
                            </p>
                        </div>
                    </transition>

                    <!-- Cancelled State -->
                    <transition name="fade">
                        <div
                            v-if="message_state === MESSAGE_STATES.CANCELLED"
                            class="text-center mt-8 mb-4"
                        >
                            <v-icon
                                color="primary"
                                size="32"
                                class="mb-0"
                                aria-hidden="true"
                            >
                                mdi-cancel
                            </v-icon>
                            <div class="text-h6 primary--text font-weight-bold">
                                Sending Cancelled
                            </div>
                            <template v-if="bulk_message.cancellation_reason">
                                <p class="mb-0"><strong>Reason:</strong> {{ bulk_message.cancellation_reason }}</p>

                                <v-alert
                                    v-if="bulk_message.is_cancelled_due_to_high_error_rate"
                                    type="info"
                                    class="high-error-rate-alert py-4 mt-4 mb-6 mx-auto text-left"
                                    outlined
                                    dense
                                >
                                    <p class="text-body-2 mb-2">
                                        We know things don't always go to plan, but we're here to help you get to the bottom of it!
                                    </p>
                                    <p class="text-body-2 my-3">
                                        <strong>Important:</strong> Do <strong>not</strong> simply retry sending this message.
                                        Repeated failed attempts can harm your sending reputation.
                                    </p>
                                    <div class="text-body-2">
                                        <strong>Next Steps:</strong>
                                        <ol class="mt-1 mb-0 pl-8">
                                            <li><strong>Download the report</strong> below to review failed recipients and understand why the message failed.</li>
                                            <li>If the issue is something you can fix (e.g., too many landlines), copy your message, correct the recipient list, and try sending again.</li>
                                            <li>If the failure was due to a spam block or you're unsure how to proceed, <strong>contact our team</strong> at <a class="primary--text" href="mailto:guide@redchirp.com">guide@redchirp.com</a> or text <strong>602-899-1365</strong> for assistance.</li>
                                        </ol>
                                    </div>
                                </v-alert>
                            </template>

                            <p class="text-body-2 mt-6 mb-0">For complete details, <rc-link
                                    inline
                                    :open_in_new_tab="false"
                                    text_transform="none"
                                    class="text-body-2"
                                    @click="$emit('export-recipients-to-excel')"
                                >
                                    download the report
                                </rc-link>.
                            </p>
                        </div>
                    </transition>

                    <!-- Finished State -->
                    <transition name="fade">
                        <div
                            v-if="message_state === MESSAGE_STATES.FINISHED"
                            class="text-center mt-8 mb-4"
                        >
                            <v-icon
                                color="success"
                                size="32"
                                class="mb-0"
                                aria-hidden="true"
                            >
                                mdi-check-circle
                            </v-icon>
                            <div class="text-h6 success--text font-weight-bold">
                                Sending Finished
                            </div>

                            <p class="text-body-2 mt-1 mb-0">For complete details, <rc-link
                                    inline
                                    :open_in_new_tab="false"
                                    text_transform="none"
                                    class="text-body-2"
                                    @click="$emit('export-recipients-to-excel')"
                                >
                                    download the report
                                </rc-link>.
                            </p>
                        </div>
                    </transition>
                </template>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import {
    MESSAGE_STATES,
    RECIPIENT_STATES,
    getActiveRecipientStates,
    getInactiveReason,
} from '@/js/bulk/bulk-states.js';
import SelectedFiltersSummary from '@/components/bulk/selected-filters-summary.vue';

export default {
    name: "RecipientsTracker",

    components: {
        SelectedFiltersSummary,
    },

    props: {
        organization_id: {
            type: String,
            required: true
        },
        bulk_message: {
            type: Object,
            required: true
        },
        bulk_message_id: {
            type: String,
            required: true
        },
        message_state: {
            type: String,
            required: true,
        },
        has_sent: {
            type: Boolean,
            required: true
        },
        progress_detail: {
            type: Object,
            required: false
        }
    },

    data() {
        return {
            MESSAGE_STATES,
            RECIPIENT_STATES,

            progress_detail_interval: null,
            submitting_notification_request: false,
        }
    },

    computed: {
        active_card_keys() {
            return getActiveRecipientStates(this.message_state);
        },

        has_opted_into_ready_for_review_notification() {
            const current_user_id = this.$rc.$state.getEmployeeId();
            return this.bulk_message.notification_employee_ids.includes(current_user_id);
        },

        progress_make_ready_to_review() {
            if (!this.progress_detail) return {};
            return this.progress_detail.progress_make_ready_to_review;
        },

        header_data() {
            if (!this.progress_detail) return {};
            return this.progress_detail.header_data || {};
        },

        header_data_cards() {
            const cards = [
                // Never display, at least for now
                // {
                //     key: 'total',
                //     label: 'Total',
                //     count: this.header_data.count_total || 0,
                //     tooltip: 'The original number of recipients that were selected when the message was created',
                //     background: '#fff',
                //     border: '#e0e0e0',
                // },
                {
                    key: RECIPIENT_STATES.PENDING,
                    label: 'Draft',
                    count: this.header_data.count_draft || 0,
                    tooltip: 'The number of recipients yet to be run through the pre-send filters.',
                    background: '#fff',
                    border: '#e0e0e0',
                },
                {
                    key: RECIPIENT_STATES.FILTERED,
                    label: 'Filtered',
                    count: this.header_data.count_filtered || 0,
                    tooltip: `The number of recipients RedChirp will not attempt to send the message to, based on the filters you selected.`,
                    background: '#fff',
                    border: '#e0e0e0',
                },
                {
                    key: RECIPIENT_STATES.READY_TO_SEND,
                    label: 'Ready to Send',
                    count: this.header_data.count_ready_to_send || 0,
                    tooltip: 'The number of recipients RedChirp will attempt to send the message to, based on the filters you selected.',
                    background: '#fff',
                    border: '#e0e0e0',
                },
                {
                    key: RECIPIENT_STATES.SENT,
                    label: 'Sent',
                    count: this.header_data.count_sent || 0,
                    tooltip: 'The number of recipients who were successfully sent the message',
                    background: '#fff',
                    border: '#e0e0e0',
                },
                {
                    key: RECIPIENT_STATES.FAILED,
                    label: 'Send Failed',
                    count: this.header_data.count_failed || 0,
                    tooltip: 'The number of recipients RedChirp attempted to send the message to, but the send failed',
                    background: '#fff',
                    border: '#e0e0e0',
                    admin_only: true, // TODO: temp
                },
                {
                    key: RECIPIENT_STATES.CANCELLED,
                    label: 'Cancelled',
                    count: this.header_data.count_cancelled || 0,
                    tooltip: 'The number of recipients whose messages were cancelled',
                    background: '#fff',
                    border: '#e0e0e0',
                },
            ];

            return cards
                .map(card => {
                    const inactive = !this.active_card_keys.includes(card.key);
                    return {
                        ...card,
                        inactive,
                        inactive_reason: getInactiveReason(this.message_state, card.key)
                    };
                })
                .filter(card => {
                    if (card.admin_only && !this.$rc.$state.isAuthActorSystemAdmin()) {
                        return false;
                    }
                    // only show cancelled when > 0; always hide in prepare/review states.
                    if (card.key === RECIPIENT_STATES.CANCELLED) {
                        return ![MESSAGE_STATES.PREPARE, MESSAGE_STATES.REVIEW].includes(this.message_state) && card.count > 0;
                    }
                    return true;
                });
        },

        count_in_state_detail() {
            if (!this.progress_detail) return [];
            return this.progress_detail.count_in_state_detail || [];
        },

        count_in_state_detail_with_values() {
            return this.count_in_state_detail
                .filter(({ state, count_in_state_detail }) => state === RECIPIENT_STATES.FILTERED && count_in_state_detail > 0)
                .sort((a, b) => b.count_in_state_detail - a.count_in_state_detail);
        },

        filter_data() {
            return this.count_in_state_detail_with_values.map(f => f.count_in_state_detail);
        },

        filter_table_rows() {
            const total = this.filter_data.reduce((a, b) => a + b, 0);
            return this.count_in_state_detail_with_values.map(f => ({
                label: f.state_detail_description,
                value: f.count_in_state_detail,
            }));
        }
    },

    methods: {
        onPause() {
            this.$emit('pause', () => {
                this.$rc.snackbar('Sending paused');
                this.updateProgressDetail();
            });
        },

        onClickCancel() {
            this.cleanUpPolling();

            // was actively sending - pause with prompt callback
            if (this.message_state === MESSAGE_STATES.SEND) {
                this.$emit('pause', () => this.showCancelConfirmDialog(true));
                return;
            }

            // wasn't actively sending - just prompt
            this.showCancelConfirmDialog();
        },

        showCancelConfirmDialog(auto_paused = false) {
            const message = auto_paused
                ? 'Sending has been paused while you decide. Are you sure you want to cancel sending this message? This action cannot be undone.'
                : 'Are you sure you want to cancel sending this message? This action cannot be undone.';

            const undo_btn = auto_paused
                ? { text: 'Resume Sending', value: 'resume' }
                : { text: 'Go Back', value: 'undo' };

            this.$rc.confirm(
                'Cancel Sending',
                message,
                [
                    undo_btn,
                    { text: 'Cancel Message', value: 'confirm' },
                ],
                (response) => {
                    if (response === 'confirm') {
                        this.onCancel();
                    } else if (response === 'resume') {
                        this.onResume();
                    }
                    // no-op on 'undo'
                }
            );
        },

        onCancel() {
            this.cleanUpPolling();
            this.$emit('cancel', this.updateProgressDetail);
        },

        onResume() {
            this.cleanUpPolling();
            this.$emit('resume', this.startPolling);
        },

        onComplete() {
            this.cleanUpPolling();
            this.$emit('update');
        },

        startPolling() {
            if (this.progress_detail_interval) return;
            this.progress_detail_interval = setInterval(this.updateProgressDetail, 3000);
        },

        cleanUpPolling() {
            if (this.progress_detail_interval) {
                clearInterval(this.progress_detail_interval);
                this.progress_detail_interval = null;
            }
        },

        updateProgressDetail() {
            this.$emit('update-progress-detail', (data) => {

                // when in pre-send states ...
                if ([MESSAGE_STATES.PREPARE, MESSAGE_STATES.REVIEW].includes(this.message_state) && !data.progress_make_ready_to_review) { // null means complete
                    this.onComplete()
                }

                // when in post-send states ...
                if ([MESSAGE_STATES.SEND, MESSAGE_STATES.PAUSED, MESSAGE_STATES.FINISHED, MESSAGE_STATES.CANCELLED].includes(this.message_state) && !data.progress_send) { // null means complete
                    this.onComplete()
                }
            });
        },

        onRequestReadyForReviewNotification() {
            this.submitting_notification_request = true;

            this.$rc.rest({
                spinner: false,
                endpoint: 'PUT: /api/v1/bulk/messages/add-notify-employee',

                params: {
                    bulk_message_id: this.bulk_message_id,
                    employee_id: this.$rc.$state.getEmployeeId(),
                },

                on_success: (data) => {
                    this.$emit('update');
                    this.$nextTick(() => {
                        this.submitting_notification_request = false;
                    });
                },

                on_error: (error) => {
                    this.submitting_notification_request = false;
                    this.$rc.alert('Error', 'Unable to add you to the notification list for this bulk message');
                }
            });
        },
    },

    mounted() {
        this.updateProgressDetail();
        this.startPolling();
    },

    beforeDestroy() {
        this.cleanUpPolling();
    },
}
</script>

<style lang="scss" scoped>
h2 {
    line-height: 1.4;
    font-size: 1.4rem;
}

.high-error-rate-alert {
    max-width: 1080px;
    border-left: 5px solid var(--v-info-base) !important;
    background-color: white !important;
}

.header-data {
    margin: 1.5rem auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.25rem;

    @media (min-width: 960px) {
        flex-direction: row;
        justify-content: center;
        align-items: stretch;
    }

    &-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        margin: 0;
        width: 100%;
        max-width: 500px;
        border-radius: 8px;
        padding: 1.5rem;
        transition: transform 0.2s ease, box-shadow 0.2s ease;

        &[type="button"] {
            appearance: none;
            background: none;
            border: none;
            font: inherit;
            color: inherit;
        }

        &--inactive {
            opacity: 0.4;

            &:hover {
                transform: none;
                box-shadow: none;
            }
        }

        &--admin-only {
            border-left: 3px solid #e53935 !important;
        }

        &--loading {
            background: #fff;
            border: 2px solid #e0e0e0;
            height: 120px;
        }

        &:not(.header-data-item--inactive):hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);

            @media (min-width: 960px) {
                transform: translateX(-2px);
            }
        }

        &.clickable {
            cursor: pointer;

            &:hover {
                box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
            }
        }

        .number-container {
            position: relative;
            display: inline-block;
            min-width: 1ch;
        }

        @media (min-width: 960px) {
            min-width: 100px;
            max-width: 300px;
            flex-direction: row;
        }
    }
}

.filter-breakdown-table {
    max-width: 500px;
    background: transparent !important;

    :deep(tbody tr:nth-of-type(even)) {
        background-color: transparent;
    }

    :deep(th) {
        font-weight: 600 !important;
        font-size: 0.75rem !important;
        color: rgba(0, 0, 0, 0.6) !important;
    }

    :deep(td) {
        font-size: 0.875rem !important;
    }

    :deep(tbody tr:hover) {
        background: rgba(0, 0, 0, 0.02) !important;
    }
}
</style>